import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { LoginInput, RegisterInput, RegisterResult } from '@socialsoup/shared';
import { api } from '@/lib/api';
import type { ApiRequestError } from '@/lib/api';
import { beginAuthAttempt, getRefreshToken, invalidateAuthAttempts } from '@/lib/auth';
import { useAuthStore } from '@/stores/authStore';
import { ROLE_TO_PORTAL } from '@/lib/constants';
import { completeSignIn, type SignInSession } from '@/features/auth/lib/complete-sign-in';

/** The login response plus the attempt epoch it was issued under (theme-customization A3). */
type LoginResult = SignInSession & { attempt: number };

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, isAuthenticated, clearAuth } = useAuthStore();

  const login = useMutation<LoginResult, ApiRequestError, LoginInput>({
    mutationFn: async (data) => {
      // The epoch is allocated BEFORE the request, so a second submit (or a logout) while this
      // one is in flight can be detected when it returns.
      const attempt = beginAuthAttempt();
      const response = await api.post<SignInSession>('/api/v1/auth/login', data);
      return { ...response.data, attempt };
    },
    onSuccess: (data) => {
      // A stale success commits nothing and does not navigate (see `completeSignIn`).
      if (!completeSignIn(data, data.attempt, queryClient)) return;
      const portalPath = ROLE_TO_PORTAL[data.user.role] ?? '/';
      navigate(portalPath);
    },
  });

  const register = useMutation<RegisterResult, ApiRequestError, RegisterInput>({
    mutationFn: async (data) => {
      const response = await api.post<RegisterResult>('/api/v1/auth/register', data);
      return response.data;
    },
    onSuccess: (result) => {
      // Register never signs anyone in (audit A-8). A self-registered CLIENT/AGENT is created
      // inactive and waits for an admin, so the login page must not tell them to sign in yet.
      navigate(result.pendingApproval ? '/login?registered=pending' : '/login?registered=true');
    },
  });

  const logout = useMutation<void, ApiRequestError, void>({
    mutationFn: async () => {
      // CAPTURE, THEN CLEAR, THEN REVOKE (theme-customization A3).
      //
      // The local session ends SYNCHRONOUSLY at logout start — auth cleared, in-flight auth
      // attempts invalidated, the old account's query cache dropped and the browser sent to the
      // login page — so nothing can keep rendering or fetching with credentials the user has
      // asked to give up, however slow the server round-trip is.
      //
      // The revoke is then sent with the credentials captured a moment ago (the store no longer
      // holds them) and with the refresh/retry path disabled: a 401 from a session that is
      // deliberately ending must not spend a rotation or bounce anyone anywhere.
      //
      // There is NO `onSettled`. The previous version cleared auth, cleared the cache and
      // navigated when the revoke completed — unconditionally — which is precisely how a slow
      // logout for account A signed out account B minutes later.
      const { accessToken, user: current } = useAuthStore.getState();
      // Owner-bound read: a refresh token another TAB stored for a different account is not this
      // logout's to revoke, and sending it would revoke a session nobody asked to end.
      const refreshToken = getRefreshToken(current?.id ?? null);

      invalidateAuthAttempts();
      clearAuth();
      queryClient.clear();
      navigate('/login');

      try {
        // R-13(b): send the stored refreshToken so the API revokes the exact
        // token; the API also tolerates it being absent by falling back to
        // the bearer-authenticated user.
        await api.post(
          '/api/v1/auth/logout',
          refreshToken ? { refreshToken } : undefined,
          // ONE option: sending captured credentials and refusing to refresh are the same
          // request. An empty captured token still selects the revoke shape — it sends no
          // Authorization header (the API falls back to the body's refreshToken) and, critically,
          // still refuses to refresh a session that is ending.
          { revoke: { token: accessToken ?? '' } },
        );
      } catch {
        // Logout already succeeded client-side; the server-side revoke is best effort.
      }
    },
  });

  return {
    user,
    isAuthenticated,
    login,
    register,
    logout,
  };
}
